import CopyablePhone from '@/components/copyable-phone';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import type { IClientUssd, IPhone } from '@/entities/client.entity';
import { socket } from '@/api/socket';
import { SOCKET_EVENTS } from '@/api/socket-events';
import { cn } from '@/lib/utils';
import { AlertTriangleIcon, PhoneForwardedIcon } from 'lucide-react';
import { useState } from 'react';
import TimeAgo from 'react-timeago';
import russianStrings from 'react-timeago/lib/language-strings/ru';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';
import { toast } from 'sonner';

const formatter = buildFormatter(russianStrings);

interface ClientUssdCardProps {
	ussd: IClientUssd;
	phones?: IPhone[];
	onCopy: (text: string) => void;
	className?: string;
}

function matchPhonesByOperator(operator: string | undefined, phones: IPhone[]): IPhone[] {
	if (!operator?.trim()) return [];
	const needle = operator.trim().toLowerCase();
	return phones.filter(phone => phone.name?.toLowerCase().includes(needle));
}

function resolveTargetPhone(ussd: IClientUssd, phones: IPhone[]): IPhone | IPhone[] | null {
	const targetPhones = phones.filter(phone => phone.id != null);
	if (targetPhones.length === 0) return null;
	if (targetPhones.length === 1) return targetPhones[0]!;

	const matched = matchPhonesByOperator(ussd.operator, targetPhones);
	if (matched.length === 1) return matched[0]!;
	if (matched.length > 1) return matched;
	return targetPhones;
}

export default function ClientUssdCard({ ussd, phones = [], onCopy, className }: ClientUssdCardProps) {
	const title = ussd.operator || ussd.usedCommand || 'USSD';
	const candidates = ussd.candidateNumbers?.filter(Boolean) ?? [];
	const hasMultipleCandidates = candidates.length > 1;
	const targetPhones = phones.filter(phone => phone.id != null);

	const [pickPhoneOpen, setPickPhoneOpen] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [pendingCandidate, setPendingCandidate] = useState<string | null>(null);
	const [pendingPhone, setPendingPhone] = useState<IPhone | null>(null);
	const [phoneChoices, setPhoneChoices] = useState<IPhone[]>([]);

	function resetDialogs() {
		setPickPhoneOpen(false);
		setConfirmOpen(false);
		setPendingCandidate(null);
		setPendingPhone(null);
		setPhoneChoices([]);
	}

	function openConfirm(candidate: string, phone: IPhone) {
		setPendingCandidate(candidate);
		setPendingPhone(phone);
		setConfirmOpen(true);
	}

	function applyCandidate(number: string) {
		if (!socket) {
			toast.error('Нет соединения');
			return;
		}
		if (targetPhones.length === 0) {
			toast.error('SIM не найдена');
			return;
		}

		const resolved = resolveTargetPhone(ussd, phones);
		if (!resolved) {
			toast.error('SIM не найдена');
			return;
		}
		if (Array.isArray(resolved)) {
			setPhoneChoices(resolved);
			setPendingCandidate(number);
			setPickPhoneOpen(true);
			return;
		}
		openConfirm(number, resolved);
	}

	function handlePickPhone(phone: IPhone) {
		if (!pendingCandidate) return;
		setPickPhoneOpen(false);
		openConfirm(pendingCandidate, phone);
	}

	function handleConfirmSetNumber() {
		if (!socket || !pendingPhone?.id || !pendingCandidate) return;
		socket.emit(SOCKET_EVENTS.setPhoneNumber, { phoneId: pendingPhone.id, number: pendingCandidate });
		resetDialogs();
	}

	const confirmSimLabel = pendingPhone
		? `${pendingPhone.name ?? 'SIM'}${pendingPhone.simSlotIndex != null ? ` (слот ${pendingPhone.simSlotIndex + 1})` : ''}`
		: '';

	return (
		<>
			<div className={cn('rounded-lg border border-border bg-card p-2.5 shadow-sm border-l-[3px] border-l-chart-5', className)}>
				<div className='flex items-start gap-2'>
					<div className='flex size-7 shrink-0 items-center justify-center rounded-lg bg-chart-5/15 text-chart-5'>
						<PhoneForwardedIcon size={14} />
					</div>

					<div className='min-w-0 flex-1'>
						<div className='flex items-center justify-between gap-2'>
							<button
								type='button'
								className='text-sm font-semibold text-chart-5 hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'
								onClick={() => onCopy(title)}
							>
								{title}
							</button>
							<span className='text-xs text-muted-foreground shrink-0'>
								<TimeAgo date={ussd.createdAt} formatter={formatter} />
							</span>
						</div>

						{ussd.phone ? (
							<CopyablePhone
								value={ussd.phone}
								onCopy={onCopy}
								prefix='Номер: '
								className='mt-1 text-xs text-muted-foreground truncate rounded-xs px-0.5'
							/>
						) : null}

						{hasMultipleCandidates ? (
							<div className='mt-2 space-y-1.5'>
								<p className='flex items-start gap-1 text-xs text-amber-600 dark:text-amber-500'>
									<AlertTriangleIcon size={12} className='mt-0.5 shrink-0' />
									Выбран первый номер — возможны другие на этом USSD; проверьте и выберите верный.
								</p>
								<div className='flex flex-wrap gap-1'>
									{candidates.map(candidate => (
										<Button
											key={candidate}
											type='button'
											variant={candidate === ussd.phone ? 'secondary' : 'outline'}
											size='sm'
											className='h-7 px-2 text-xs'
											onClick={() => applyCandidate(candidate)}
										>
											{candidate}
										</Button>
									))}
								</div>
							</div>
						) : null}

						{ussd.failureCode != null && ussd.failureCode !== 0 ? (
							<p className='mt-1 text-xs text-destructive'>Код ошибки: {ussd.failureCode}</p>
						) : null}
					</div>
				</div>

				<button
					type='button'
					className='mt-1.5 block w-full text-left rounded-2xl rounded-tl-sm bg-muted/50 px-2.5 py-2 hover:bg-muted/70 cursor-pointer select-none'
					onClick={() => onCopy(ussd.response || ussd.usedCommand || '')}
				>
					<p className='text-sm leading-relaxed text-muted-foreground'>{ussd.response || ussd.usedCommand || '—'}</p>
				</button>
			</div>

			<Dialog open={pickPhoneOpen} onOpenChange={open => !open && resetDialogs()}>
				<DialogContent className='sm:max-w-[400px]'>
					<DialogHeader>
						<DialogTitle>Выберите SIM</DialogTitle>
						<DialogDescription>Несколько SIM — укажите, для какой установить номер.</DialogDescription>
					</DialogHeader>
					<div className='space-y-2'>
						{phoneChoices.map(phone => (
							<Button
								key={phone.id}
								type='button'
								variant='outline'
								className='w-full justify-start h-auto py-2'
								onClick={() => handlePickPhone(phone)}
							>
								<span className='text-left'>
									<span className='block font-medium'>{phone.name ?? 'SIM'}</span>
									<span className='block text-xs text-muted-foreground'>
										{phone.simSlotIndex != null ? `Слот ${phone.simSlotIndex + 1}` : 'Слот —'}
										{phone.number ? ` · ${phone.number}` : ''}
									</span>
								</span>
							</Button>
						))}
					</div>
				</DialogContent>
			</Dialog>

			<Dialog open={confirmOpen} onOpenChange={open => !open && resetDialogs()}>
				<DialogContent className='sm:max-w-[400px]'>
					<DialogHeader>
						<DialogTitle>Установить номер?</DialogTitle>
						<DialogDescription>
							Установить номер <span className='font-mono font-medium'>{pendingCandidate}</span> для{' '}
							<span className='font-medium'>{confirmSimLabel}</span>?
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button type='button' variant='outline' onClick={resetDialogs}>
							Отмена
						</Button>
						<Button type='button' onClick={handleConfirmSetNumber}>
							Подтвердить
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
